/**
 * signInRefusal — what a refused sign-in SAYS and what it DOES to the client throttle
 * (LIFE-SHORTLISTED-001 LOGIN-CONVERGENCE W3 + W7).
 *
 * One judgement for every sign-in the shell renders, replacing three: the owner console's
 * `useOwnerConsoleLogin` and purchase-web's `StorefrontSecureForm` each worded a refusal by its CODE
 * and synchronised the throttle with the server's lockout, while `useLoginForm` counted EVERY thrown
 * error as a failed attempt and rendered whatever English string the auth context had stored.
 *
 * The reading, in order:
 *   1. The server's own throttle refused (`RATE_LIMIT_EXCEEDED` with a positive integer
 *      `errorDetails.retryAfter`, seconds): the ONE client throttle locks for exactly that long
 *      (`useRateLimit().lockFor`), so `<RateLimitNotice>` shows one refusal and one countdown
 *      whichever side refused. No sentence is added — the notice IS the sentence.
 *   2. The words: the SURFACE's own map (`refusalMessages`, already translated, keyed by the server's
 *      `errorCode`) first; then this package's catalog for the codes every sign-in server in the
 *      fleet answers; then the thrown `AuthError`'s own catalog key (the status classes
 *      `translateAuthError` assigns); otherwise none, and the form falls back to the auth context's
 *      `error`.
 *   3. The attempt: a refusal the server NAMED (by a code in either map) is not a refused credential
 *      unless the code is a credential code (`credentialRefusalCodes`, default
 *      `INVALID_CREDENTIALS`), so it spends no attempt. An unnamed failure spends one — fail closed:
 *      a failure nobody classified is never assumed harmless.
 *
 * A refusal entry in `refusalMessages` is a translated sentence, or `{ message, locked: true }` for a
 * TERMINAL refusal the form renders as the locked banner (W8) — the account or credential is locked,
 * and only something outside the form can lift it.
 *
 * @module @puffin-core/login-shell/utils/signInRefusal
 */

// The server's throttle code and its lockout reader are `@puffin-core/form-security`'s since
// LIFE-SHORTLISTED-001 D13 (2026-10-13): palm-native's sign-in reads the server's lockout by the same rule.
import { SERVER_RATE_LIMIT_CODE, serverLockoutMs } from '@puffin-core/form-security/core';
import { ENROLLMENT_FACTORS, FACTOR_SETUP_REQUIRED } from '@puffin-core/factor-enrollment-codes';

/**
 * The refusal codes every sign-in server in the fleet can answer, and the `login`-namespace leaf
 * that words each. Sources, measured 2026-10-11: `@puffin-core/consumer-auth` (INVALID_CREDENTIALS,
 * ACCOUNT_NOT_ACTIVE, SIGN_IN_UNAVAILABLE), `@puffin-core/auth-routes` (ACCOUNT_NOT_ACTIVE), `@puffin-core/turnstile` +
 * purchase-api's honeypot (CAPTCHA_FAILED), `@puffin-core/middleware` (RATE_LIMIT_EXCEEDED).
 *
 * A LOCK has no entry (OWNER, 2026-10-21, "Zero leak + lock email"): a locked account answers exactly as invalid
 * credentials, and the person is told out of band. `CREDENTIAL_LOCKED` / `ACCOUNT_LOCKED` and their leaf were pruned.
 */
export const SIGN_IN_REFUSAL_KEYS = Object.freeze({
  INVALID_CREDENTIALS: 'login.error.credentials',
  CAPTCHA_FAILED: 'login.refusal.captchaFailed',
  ACCOUNT_NOT_ACTIVE: 'login.refusal.accountNotActive',
  // `@puffin-core/auth-routes` `SIGN_IN_REFUSAL_CODES` (the D1 errorCode burn-down). None proves a wrong secret, so none
  // spends an attempt; before they were named here, every app's form read them as "invalid credentials" and spent one.
  // An inactive account means what `ACCOUNT_NOT_ACTIVE` means, and is worded by the same leaf (the codes fold later).
  ACCOUNT_INACTIVE: 'login.refusal.accountNotActive',
  COMPANY_INACTIVE: 'login.refusal.companyInactive',
  ACCOUNT_UNAVAILABLE: 'login.refusal.accountUnavailable',
  SIGN_IN_UNAVAILABLE: 'login.refusal.signInUnavailable',
  // LOGIN-CONVERGENCE (coordinator ruling (a)): MFA on, but no enrolled method this app can serve. Not a credential
  // refusal — the password was right — so it spends no attempt.
  MFA_NO_USABLE_METHOD: 'login.refusal.mfaNoUsableMethod',
  // SECREQ S6: a sign-in HOLD — the credential was right, and the company MFA policy owes a factor first. A STATE, never a
  // failed attempt, wherever a hold reaches a form as a refusal (pos-web's own sign-in legs among them).
  [FACTOR_SETUP_REQUIRED.mfa]: 'login.error.mfaEnrollmentRequired',
  [FACTOR_SETUP_REQUIRED.passkey]: 'login.error.passkeySetupRequired',
  [FACTOR_SETUP_REQUIRED.pin]: 'login.error.pinSetupRequired',
  [SERVER_RATE_LIMIT_CODE]: 'login.error.rateLimit',
});

// Every factor a sign-in can be HELD for has its sentence here, or this module refuses to load (G16): a vocabulary that
// grew a factor with no sentence would hold a person with a blank refusal. Judged at LOAD, outside any sign-in's catch,
// so the bug is never read as a refused attempt; a form reads a hold's key with no fallback.
for (const factor of ENROLLMENT_FACTORS) {
  const code = FACTOR_SETUP_REQUIRED[factor];
  if (typeof SIGN_IN_REFUSAL_KEYS[code] !== 'string') {
    throw new TypeError(
      `signInRefusal: the held factor "${factor}" (${code}) has no SIGN_IN_REFUSAL_KEYS sentence`,
    );
  }
}

/** The default credential codes: a refusal that proves a wrong secret, and so spends an attempt. */
export const DEFAULT_CREDENTIAL_REFUSAL_CODES = Object.freeze(['INVALID_CREDENTIALS']);

/**
 * The surface's entry for a code, normalised to `{ message, locked }`, or null.
 *
 * @param {Object<string, (string|{message: string, locked?: boolean})>} refusalMessages
 * @param {?string} code
 * @returns {{message: string, locked: boolean}|null}
 */
function surfaceEntry(refusalMessages, code) {
  if (!code || !Object.hasOwn(refusalMessages, code)) return null;
  const entry = refusalMessages[code];
  if (typeof entry === 'string') return { message: entry, locked: false };
  return { message: entry.message, locked: entry.locked === true };
}

/**
 * Read a refused sign-in.
 *
 * @param {unknown} err - What `auth.login` threw: an `AuthError` (`translateAuthError`), an
 *   `ApiError`, or the auth client's `Error{status, errorCode, errorDetails}`.
 * @param {Object} [options]
 * @param {Object<string, (string|{message: string, locked?: boolean})>} [options.refusalMessages={}]
 * @param {ReadonlyArray<string>} [options.credentialRefusalCodes=DEFAULT_CREDENTIAL_REFUSAL_CODES]
 * @returns {{
 *   errorCode: ?string,
 *   lockoutMs: ?number,
 *   spendsAttempt: boolean,
 *   message: ?string,
 *   messageKey: ?string,
 *   locked: boolean,
 * }} `message` is a sentence the surface already translated; `messageKey` a `login`-namespace leaf
 *   the form translates. At most one of the two is set; neither, when nothing names the failure.
 */
export function describeSignInRefusal(
  err,
  { refusalMessages = {}, credentialRefusalCodes = DEFAULT_CREDENTIAL_REFUSAL_CODES } = {},
) {
  const errorCode = typeof err?.errorCode === 'string' ? err.errorCode : null;
  const lockoutMs = serverLockoutMs(err);
  const surface = surfaceEntry(refusalMessages, errorCode);
  const catalogKey =
    errorCode && Object.hasOwn(SIGN_IN_REFUSAL_KEYS, errorCode)
      ? SIGN_IN_REFUSAL_KEYS[errorCode]
      : null;
  const named = Boolean(surface || catalogKey);
  const spendsAttempt =
    lockoutMs === null && (!named || credentialRefusalCodes.includes(errorCode));

  // The server's lockout is told by `<RateLimitNotice>` and its countdown; a sentence beside it
  // would say the same thing twice.
  if (lockoutMs !== null) {
    return { errorCode, lockoutMs, spendsAttempt, message: null, messageKey: null, locked: false };
  }
  if (surface) {
    return {
      errorCode,
      lockoutMs,
      spendsAttempt,
      message: surface.message,
      messageKey: null,
      locked: surface.locked,
    };
  }
  const messageKey = catalogKey ?? (typeof err?.messageKey === 'string' ? err.messageKey : null);
  return { errorCode, lockoutMs, spendsAttempt, message: null, messageKey, locked: false };
}
